/**
 * Блок «Улучшить» на карточке результата: Magnific Precise (Krea) поверх готовой картинки, платно.
 * Кнопка — у отмеченного «В улучшение» или загруженного, пока нет улучшения в работе или готового (`lib/enhanceGate.ts`, правило сервера); иначе — подсказка.
 */
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { btnPrimary, ErrorLine } from "@/components/trends/ui";
import { editRunView } from "@/lib/editRuns";
import { thumbUrl } from "@/lib/media";
import { enhanceVariant, resultPath, type ResultCard } from "@/lib/results";

type Runs = NonNullable<ResultCard["enhances"]>;

export function EnhancePanel({
  variantId,
  ready,
  hint,
  runs,
  onStarted,
}: {
  variantId: number;
  /** на сервере есть ключ Krea */
  ready: boolean;
  /** почему улучшать нельзя (`enhanceHint`); `null` — кнопка */
  hint: string | null;
  runs: Runs;
  onStarted: () => void;
}) {
  /** id запущенного улучшения: пока его нет в `runs`, показываем «Запущено» */
  const [launched, setLaunched] = useState<number | null>(null);
  /** один ключ на клик: повтор того же запроса сервер не оплатит дважды */
  const [key, setKey] = useState(() => crypto.randomUUID());

  const start = useMutation({
    mutationFn: () => enhanceVariant(variantId, { idempotency_key: key }),
    onSuccess: (r) => {
      setLaunched(r.generation_id);
      setKey(crypto.randomUUID());
      onStarted();
    },
  });

  return (
    <div className="grid gap-2">
      {hint ? (
        <div className="text-[12px] text-fg-muted" data-annot="улучшить нельзя">
          {hint}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={btnPrimary} disabled={!ready || start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? "Ставлю…" : "Улучшить"}
          </button>
          {ready ? null : <span className="text-[12px] text-err">нет ключа Krea</span>}
        </div>
      )}
      <div className="text-[12px] text-fg-muted">Magnific Precise 2x · резкость 7 · зерно 7 · детали 30</div>
      <ErrorLine error={start.error} />
      {launched !== null && !runs.some((r) => r.generation_id === launched) ? (
        <div className="text-[12px] text-fg-muted">Запущено — результат появится здесь</div>
      ) : null}
      {runs.length ? (
        <ul className="grid gap-2">
          {[...runs]
            .sort((a, b) => b.generation_id - a.generation_id)
            .map((r) => {
              const v = editRunView(r);
              return (
                <li key={r.generation_id} className="grid gap-1 text-[12px]">
                  {v.state === "done" ? (
                    <div className="flex flex-wrap gap-1">
                      {v.items.map((it) => (
                        <Link key={it.variantId} to={resultPath(it.variantId)} title={`вариант #${it.variantId}`}>
                          <img
                            src={thumbUrl(it.path)}
                            alt={`вариант #${it.variantId}`}
                            loading="lazy"
                            className="size-16 rounded-[4px] border border-border object-cover"
                          />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span className={v.state === "failed" ? "text-err" : "text-fg-muted"}>{v.label}</span>
                  )}
                </li>
              );
            })}
        </ul>
      ) : null}
    </div>
  );
}
